/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';
import template from './labels-component.html';
import {OBJECT_TYPE} from "../../constants/object-type";
import {AdnFormStateService} from '../../../../components/util/adn-form-state.service';
import {StateService} from '@uirouter/core';
import {firstValueFrom} from 'rxjs';
import {SearchApiService} from '../../controller/search-api.service';

function coerceBooleanAttr(value: any, defaultValue = false): boolean {
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  if (value === '') {
    return true;
  }
  return defaultValue;
}

@Component({
  standalone: false,
  selector: 'adn-resource-labels',
  template
})
export class ResourceLabelsComponent implements OnInit, OnChanges, DoCheck {
  @Input() resource: any;

  @Input() key?: string;
  @Input() transKey?: string;
  @Input() transParams?: any;
  @Input() labelType?: string;
  @Input() autoCompleteOnly?: boolean | string;
  @Input() blockSubmit?: boolean | string;
  @Input() maxTags?: number | string;
  @Output() changed = new EventEmitter<void>();

  labels: string[] = [];
  maxTagsValue = 50;
  translationParams: any = {};
  private readonly emptySourceLabels: any[] = [];
  private sourceLabels: any;

  constructor(
    private readonly stateService: StateService,
    private readonly searchApi: SearchApiService,
    @Inject(AdnFormStateService) private readonly adnFormState?: any
  ) {
  }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    void changes;
    this.initialize();
  }

  ngDoCheck(): void {
    const nextLabels = this.getSourceLabels();
    if (nextLabels !== this.sourceLabels) {
      this.refreshLabels(nextLabels);
    }
  }

  syncLabels(): void {
    if (!this.resource || !this.key) {
      return;
    }

    const nextLabels = this.labels.slice();
    const currentLabels = _.compact(_.map(_.get(this.resource, this.key) || [], (label) => {
      return `${label || ''}`.trim();
    }));

    if (_.isEqual(currentLabels, nextLabels)) {
      return;
    }

    _.set(this.resource, this.key, nextLabels);
    this.sourceLabels = nextLabels;
    if (_.isFunction(this.adnFormState?.markDirty)) {
      this.adnFormState.markDirty();
    }
    this.changed.emit();
  }

  search(label: string): void {
    const resourceType = this.resolveResourceType();
    const labelInfo: any = {};
    labelInfo[resourceType.apiParam + 's'] = label;
    this.stateService.go('app.search', labelInfo, {inherit: false});
  }

  query = (queryText: string): Promise<string[]> => {
    return firstValueFrom(this.searchApi.search({
      q: queryText,
      types: this.resolveResourceType().type
    })).then((result) => {
      return _.compact(_.map(result.searchResults, 'name'));
    });
  };

  get fieldName(): string {
    return this.key || 'model.labels';
  }

  get isAutoCompleteOnly(): boolean {
    return coerceBooleanAttr(this.autoCompleteOnly);
  }

  get isDisabled(): boolean {
    return coerceBooleanAttr(this.blockSubmit);
  }

  private initialize(): void {
    if (!this.resource) {
      return;
    }

    this.key = this.key || 'model.labels';
    this.transKey = this.transKey || _.last((this.key || '').split('.')) || 'labels';
    this.transParams = this.transParams || '{}';
    this.translationParams = this.resolveTranslationParams(this.transParams);

    const maxTags = parseInt(`${this.maxTags}`, 10);
    this.maxTagsValue = Math.min(Number.isFinite(maxTags) ? maxTags : 50, 50);

    this.refreshLabels(this.getSourceLabels());
  }

  private getSourceLabels(): any {
    return this.resource && this.key ? _.get(this.resource, this.key) || this.emptySourceLabels : this.emptySourceLabels;
  }

  private refreshLabels(sourceLabels: any): void {
    this.sourceLabels = sourceLabels;
    this.labels = _.compact(_.map(sourceLabels, (label) => {
      return `${label || ''}`.trim();
    }));
  }

  private resolveResourceType(): any {
    return OBJECT_TYPE[this.labelType || 'nothing'] || OBJECT_TYPE.Label;
  }

  private resolveTranslationParams(params: any): any {
    if (!params) {
      return {};
    }

    if (!_.isString(params)) {
      return params;
    }

    try {
      const normalized = params
        .replace(/([{,]\s*)([A-Za-z_$][\w$]*)(\s*:)/g, '$1"$2"$3')
        .replace(/'/g, '"');
      return JSON.parse(normalized) || {};
    } catch {
      return {};
    }
  }
}
